import { TextField, useRecordContext } from 'react-admin';
import React, { useState, memo, useEffect } from 'react';
import { CustomEditableFieldPropsType } from '../../../utils/DataTypes';
import { nonEditableFields, currencyFields } from '../../../constants';
import { toast } from 'react-toastify';
import { NumericFormat } from 'react-number-format';

const CustomEditableField = memo(
  ({ source, label, editRowIds, allEditedRows, handleFieldChange }: CustomEditableFieldPropsType) => {
    const record = useRecordContext();
    if (!record) {
      return <span>No Record</span>;
    }

    const isEditing = editRowIds.includes(record.id.toString());
    const currentValue = allEditedRows[record.id]?.[source] ?? record[source] ?? '';
    const [value, setValue] = useState(currentValue);
    const [hasError, setHasError] = useState(false);

    const isFieldEditable = !nonEditableFields.includes(source);
    const isFieldCurrency = currencyFields.includes(source);

    useEffect(() => {
      if (!isEditing) {
        setHasError(false);
        setValue(isFieldCurrency ? record[source]?.replace('$', '') : record[source] || '');
      }
    }, [isEditing, record[source]]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setValue(newValue);
      handleFieldChange(source, newValue, record.id.toString());
    };

    const handleCurrencyInputChange = (newValue: string | undefined) => {
      if (!newValue) {
        setValue('');
        handleFieldChange(source, '', record.id.toString());
      } else if (!isNaN(Number(newValue))) {
        setValue(newValue);
        handleFieldChange(source, newValue, record.id.toString());
      } else {
        toast.error('Please enter a valid number');
        setValue('');
        handleFieldChange(source, '', record.id.toString());
      }
    };

    const handleCurrencyKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (
        !/^[0-9.]$/.test(e.key) &&
        !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter'].includes(e.key)
      ) {
        e.preventDefault();
        setHasError(true);
        toast.error('Invalid character. Please enter numbers only (0-9) or a decimal point.');
      } else {
        setHasError(false);
      }
    };

    return (
      <div>
        {isEditing && isFieldEditable ? (
          isFieldCurrency ? (
            <NumericFormat
              prefix="$"
              id={`input_${source}`}
              name={`input_${source}`}
              value={value}
              decimalScale={2}
              allowNegative={false}
              onValueChange={(values) => {
                handleCurrencyInputChange(values.value);
              }}
              maxLength={9}
              onKeyDown={handleCurrencyKeyDown}
              style={{
                border: hasError ? '1px solid red' : '1px solid #ccc',
                outline: hasError ? '1px solid red' : undefined,
                borderRadius: '4px',
                padding: '8px',
                width: '100%',
                boxSizing: 'border-box',
              }}
            />
          ) : (
            <input
              type="text"
              value={value}
              onChange={handleChange}
              style={{
                border: '1px solid #ccc',
                borderRadius: '4px',
                padding: '8px',
                width: '100%',
                boxSizing: 'border-box',
              }}
            />
          )
        ) : (
          <>
            <TextField sortable={false} source={source} label={label} record={record} />
          </>
        )}
      </div>
    );
  }
);
export default CustomEditableField;
